/**
 * Utility for efficiently loading Font Awesome icons
 * This helps reduce the initial load by only loading icons that are needed
 */

// Map of icon classes to their corresponding Unicode values
const ICON_MAP = {
  // Social media icons (brand icons)
  'fa-facebook': '\uf09a',
  'fa-instagram': '\uf16d',
  'fa-youtube': '\uf167',
  'fa-whatsapp': '\uf232',
  
  // Common interface icons
  'fa-phone': '\uf095',
  'fa-envelope': '\uf0e0',
  'fa-map-marker-alt': '\uf3c5',
  'fa-chevron-right': '\uf054',
  'fa-chevron-left': '\uf053',
  'fa-bars': '\uf0c9',
  'fa-times': '\uf00d',
  'fa-play': '\uf04b',
  'fa-pause': '\uf04c',
  'fa-music': '\uf001'
};

// Font families for different icon types - included as a reference for the CSS
// const ICON_FAMILIES = {
//   'fab': 'Font Awesome 6 Brands', // Brand icons
//   'fas': 'Font Awesome 6 Free',   // Solid icons
//   'far': 'Font Awesome 6 Free'    // Regular icons
// };

// Default weights for different icon styles - included as a reference for the CSS
// const ICON_WEIGHTS = {
//   'fab': 400,   // Brand icons are usually regular weight
//   'fas': 900,   // Solid icons are bold
//   'far': 400    // Regular icons are normal weight
// };

/**
 * Injects critical Font Awesome icons as inline CSS to avoid render-blocking
 * @param {Array<string>} iconNames - List of icon names to preload (without the 'fa-' prefix)
 */
export function injectCriticalIcons(iconNames = []) {
  if (!iconNames || !iconNames.length) return;
  
  // Base CSS for Font Awesome
  let iconCSS = `
@font-face {
  font-family: 'Font Awesome 6 Brands';
  font-style: normal;
  font-weight: 400;
  font-display: block;
  src: url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/webfonts/fa-brands-400.woff2") format("woff2");
}

@font-face {
  font-family: 'Font Awesome 6 Free';
  font-style: normal;
  font-weight: 900;
  font-display: block;
  src: url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/webfonts/fa-solid-900.woff2") format("woff2");
}

.fab {
  font-family: 'Font Awesome 6 Brands';
  font-weight: 400;
}

.fas {
  font-family: 'Font Awesome 6 Free';
  font-weight: 900;
}

.fa, .fas, .fab, .far {
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  display: inline-block;
  font-style: normal;
  font-variant: normal;
  text-rendering: auto;
  line-height: 1;
}
`;
  
  // Create CSS for the selected icons
  iconNames.forEach(name => {
    const fullName = name.startsWith('fa-') ? name : `fa-${name}`;
    const unicode = ICON_MAP[fullName];
    
    if (unicode) {
      // Add rules for both .fa-youtube and .fab.fa-youtube
      iconCSS += `
/* Regular icon class */
.${fullName}:before {
  content: "${unicode}";
}

/* Brand icon class */
.fab.${fullName}:before {
  content: "${unicode}";
  font-family: 'Font Awesome 6 Brands';
  font-weight: 400;
}

/* Solid icon class */
.fas.${fullName}:before {
  content: "${unicode}";
  font-family: 'Font Awesome 6 Free';
  font-weight: 900;
}
`;
    }
  });
  
  // Inject the CSS if we have any icons
  if (iconCSS) {
    const style = document.createElement('style');
    style.textContent = iconCSS;
    document.head.appendChild(style);
  }
}

/**
 * Loads the full Font Awesome CSS asynchronously
 */
export function loadFullIconLibrary() {
  return new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
    link.onload = () => resolve();
    document.head.appendChild(link);
  });
}

export default {
  injectCriticalIcons,
  loadFullIconLibrary
}; 